import {
  BarChartOutlined,
  FolderOutlined,
  TeamOutlined
} from "@ant-design/icons"
import { Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { brand, neutral } from "../../../../components/widgets/tokens.ts"

const { Text, Title } = Typography

const BENEFITS = [
  { key: "build", icon: <BarChartOutlined /> },
  { key: "organize", icon: <FolderOutlined /> },
  { key: "share", icon: <TeamOutlined /> }
] as const

/** Shapes only: a report page with KPI blocks, bars and a line. No numbers,
 * so it can never be read as anyone's data. */
const ReportIllustration = () => (
  <svg
    aria-hidden
    focusable="false"
    viewBox="0 0 240 150"
    className="h-full w-auto"
  >
    <rect
      x="52"
      y="10"
      width="150"
      height="128"
      rx="10"
      fill="#fff"
      opacity=".55"
    />
    <rect
      x="34"
      y="20"
      width="160"
      height="124"
      rx="10"
      fill="#fff"
      stroke="#D6ECEF"
    />
    <rect x="48" y="34" width="38" height="20" rx="4" fill="#E6F3F5" />
    <rect x="92" y="34" width="38" height="20" rx="4" fill="#E6F3F5" />
    <rect x="136" y="34" width="44" height="20" rx="4" fill="#E6F3F5" />
    <rect
      x="48"
      y="96"
      width="9"
      height="30"
      rx="2"
      fill={brand.teal}
      opacity=".45"
    />
    <rect
      x="62"
      y="84"
      width="9"
      height="42"
      rx="2"
      fill={brand.teal}
      opacity=".6"
    />
    <rect
      x="76"
      y="90"
      width="9"
      height="36"
      rx="2"
      fill={brand.teal}
      opacity=".5"
    />
    <rect
      x="90"
      y="74"
      width="9"
      height="52"
      rx="2"
      fill={brand.teal}
      opacity=".75"
    />
    <rect x="104" y="66" width="9" height="60" rx="2" fill={brand.teal} />
    <polyline
      points="126,112 140,100 152,106 166,88 180,76"
      fill="none"
      stroke={brand.teal}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="180" cy="76" r="3.5" fill={brand.teal} />
    <circle cx="210" cy="118" r="20" fill="#fff" stroke="#D6ECEF" />
    <path
      d="M210 98 A20 20 0 0 1 228 126 L210 118 Z"
      fill={brand.teal}
      opacity=".7"
    />
  </svg>
)

/**
 * Why Reports is worth using, said once on All Reports. Compact by design:
 * the list beneath it is the page's real work, and it carries the only New
 * Report action.
 */
export const ReportsIntro = () => {
  const { t } = useTranslation()
  return (
    <section
      aria-labelledby="reports-intro-title"
      className="relative flex items-center gap-6 overflow-hidden rounded-xl border border-solid px-6 py-5"
      style={{
        borderColor: "#D6ECEF",
        background:
          "linear-gradient(100deg, rgba(42,125,140,.10) 0%, rgba(42,125,140,.04) 55%, #fff 100%)"
      }}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Text
          className="text-xs font-semibold uppercase tracking-wide"
          style={{ color: brand.teal }}
        >
          {t("reports.intro.eyebrow")}
        </Text>
        <Title
          id="reports-intro-title"
          level={3}
          className="m-0 text-start"
          style={{ color: neutral.ink }}
        >
          {t("reports.intro.headline")}
        </Title>
        <Text type="secondary" className="max-w-[640px]">
          {t("reports.intro.body")}
        </Text>
        <ul className="m-0 mt-2 flex list-none flex-wrap gap-x-6 gap-y-2 p-0">
          {BENEFITS.map((benefit) => (
            <li key={benefit.key} className="flex items-center gap-2">
              <span
                aria-hidden
                className="flex size-7 items-center justify-center rounded-md"
                style={{
                  background: "rgba(42,125,140,.12)",
                  color: brand.teal
                }}
              >
                {benefit.icon}
              </span>
              <Text className="text-[13px]">
                {t(`reports.intro.benefits.${benefit.key}`)}
              </Text>
            </li>
          ))}
        </ul>
      </div>
      <div className="hidden h-[130px] shrink-0 lg:block">
        <ReportIllustration />
      </div>
    </section>
  )
}

export { ReportIllustration }
